import cn from 'classnames';
import type { CSSProperties, FC, KeyboardEvent, MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { _isNotEmpty, _noop, _notNil } from '@/littledash';

import Button from '@/components/UI/Button';
import Icon from '@/components/UI/Icon';

import { useDevices } from '@/utils/devices/useDevices';

interface SearchByOption {
  value: string;
  display: string;
}

interface KeywordSearchFormData {
  query: string;
  by: string;
}

interface KeywordSearchProps {
  handleSearchInput?: (value: string) => void;
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  searchBy?: string;
  setSearchBy?: (by: string) => void;
  searchByOptions?: Array<SearchByOption>;
  searchPlaceholderText?: string;
  style?: CSSProperties;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  searchBarRef?: MutableRefObject<HTMLInputElement | null>;
  autoComplete?: boolean;
  fillWidth?: boolean;
}

const KeywordSearch: FC<KeywordSearchProps> = ({
  handleSearchInput = _noop,
  searchQuery,
  setSearchQuery,
  searchBy,
  setSearchBy,
  searchByOptions,
  searchPlaceholderText,
  style,
  className,
  autoFocus,
  disabled,
  searchBarRef,
  autoComplete = true,
  fillWidth = false,
}) => {
  const [placeholder, setPlaceHolder] = useState(searchPlaceholderText ?? '');
  const _searchBarRef = useRef<HTMLInputElement | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    formState: { dirtyFields },
    reset,
    setValue,
  } = useForm<KeywordSearchFormData>({ defaultValues: { query: searchQuery ?? '', by: searchBy ?? '' } });

  const onNewReading = (response: string) => {
    setValue('query', response, { shouldDirty: true, shouldTouch: true });
    handleSearchInput(response);
    nextReading(true);
  };

  const { nextReading } = useDevices({
    targetId: 'focus-search',
    onNewReading,
  });

  const [by, query] = watch(['by', 'query']);

  useEffect(() => {
    const searchByOptionDisplay = searchByOptions?.find(({ value }) => value === by)?.display ?? null;
    if (_notNil(searchByOptionDisplay) && _notNil(searchPlaceholderText)) {
      setPlaceHolder(`${searchPlaceholderText} by ${searchByOptionDisplay}`);
    } else {
      setPlaceHolder(searchPlaceholderText ?? '');
    }
  }, [by, searchPlaceholderText]);

  useEffect(() => {
    if (_notNil(setSearchQuery)) {
      setSearchQuery(query);
    }
  }, [query]);

  useEffect(() => {
    if (_isNotEmpty(by) && _notNil(setSearchBy)) {
      setSearchBy(by);
      if ((query?.trim()?.length ?? 0) > 0) {
        handleSearchInput(query);
      }
    }
  }, [by]);

  const resetButtonClickHandler = () => {
    reset({ query: '', by: searchBy ?? '' });
    handleSearchInput('');
  };

  const selectSearchInputText = () => {
    _searchBarRef?.current?.focus();
    _searchBarRef?.current?.select();
  };

  const preventEnterKeySubmission = (event: KeyboardEvent<HTMLFormElement>) => {
    const target = event.target as HTMLElement;
    if (event.key === 'Enter' && target.tagName !== 'TEXTAREA') {
      event.preventDefault();
      onSubmit();
    }
  };

  const onSubmit = () => {
    const value = query?.trim() ?? '';
    if (value.length > 0) {
      handleSearchInput(value);
    } else {
      resetButtonClickHandler();
    }
    selectSearchInputText();
  };
  const { ref: queryRef, ...queryRest } = register('query');
  const queryInputRef = (el: HTMLInputElement | null) => {
    queryRef(el);
    _searchBarRef.current = el;
    if (_notNil(searchBarRef)) {
      searchBarRef.current = el;
    }
  };

  return (
    <form
      className="relative flex flex-grow-1"
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={preventEnterKeySubmission}
      data-test-component="KeywordSearch"
      data-test-element="form"
    >
      <div className={cn('relative w-100', { 'flex items-center': fillWidth })}>
        <span className="absolute z-5" style={{ top: '13px', left: '10px' }}>
          <Icon icon="search" width="24" height="24" />
        </span>
        <span
          className={cn('relative', fillWidth ? 'db flex-grow-1' : 'mw6')}
          style={fillWidth ? { minWidth: 0, maxWidth: '28rem' } : undefined}
        >
          <input
            {...queryRest}
            ref={queryInputRef}
            disabled={disabled}
            type="text"
            data-test-element="query-input"
            data-testid="query-input"
            className={cn(className, 'ui__keyword-search-bar relative ba b--gray bg-white', {
              'w-100': fillWidth,
            })}
            style={{
              paddingRight: '4.5em',
              ...(fillWidth ? { maxWidth: 'none' } : null),
              ...style,
            }}
            placeholder={placeholder}
            autoFocus={autoFocus}
            autoComplete={autoComplete ? 'on' : 'off'}
          />
          {((searchQuery?.length ?? 0) > 0 || (dirtyFields?.query ?? false)) && (
            <Button
              onClick={resetButtonClickHandler}
              stateless
              style={{
                padding: '0',
                top: '50%',
                right: '1em',
                transform: 'translateY(-50%)',
              }}
              className="blue f6 absolute right-0"
              testId="keyword-search-reset-button"
            >
              Reset
            </Button>
          )}
        </span>
        {_isNotEmpty(searchByOptions) && (
          <select className="ml2 w4 dib" style={{ marginBottom: 0 }} data-test-element="by-select" {...register('by')}>
            {searchByOptions?.map(({ value, display }) => (
              <option key={value} value={value} data-test-element="option" data-test-key={value}>
                {display}
              </option>
            ))}
          </select>
        )}
      </div>
    </form>
  );
};

export default KeywordSearch;
